/**
 * Shared UI primitives and formatting helpers.
 *
 * Small and deliberately unclever. The console's job is to make a security picture legible under
 * time pressure, so the components here optimize for scannability — tabular numbers, consistent
 * severity treatment, and never conveying meaning by color alone.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { ApiError, type Severity } from '../api'

/* ---------------------------------------------------------------- formatting */

export function fmtTime(ms: number): string {
  if (!ms) return '—'
  return new Date(ms).toLocaleString(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  })
}

export function fmtRelative(ms: number): string {
  if (!ms) return '—'
  const delta = Date.now() - ms
  const abs = Math.abs(delta)
  const units: [number, string][] = [
    [86400000, 'd'],
    [3600000, 'h'],
    [60000, 'm'],
    [1000, 's'],
  ]
  for (const [size, label] of units) {
    if (abs >= size) {
      const n = Math.floor(abs / size)
      return delta >= 0 ? `${n}${label} ago` : `in ${n}${label}`
    }
  }
  return 'just now'
}

export function fmtBytes(n: number): string {
  if (n < 1024) return `${n} B`
  const units = ['KB', 'MB', 'GB', 'TB', 'PB']
  let v = n / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

export function fmtNumber(n: number): string {
  return n.toLocaleString()
}

export function fmtDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${Math.round(ms / 1000)}s`
  if (ms < 3600000) return `${Math.round(ms / 60000)}m`
  if (ms < 86400000) return `${(ms / 3600000).toFixed(1)}h`
  return `${Math.round(ms / 86400000)}d`
}

/* ---------------------------------------------------------------- primitives */

export function Chip({
  children,
  tone = 'neutral',
  title,
}: {
  children: ReactNode
  tone?: Severity | 'ok' | 'warn' | 'danger' | 'neutral' | 'outline'
  title?: string
}) {
  return (
    <span className={`chip ${tone}`} title={title}>
      {children}
    </span>
  )
}

/**
 * A severity chip.
 *
 * Always carries its label, never color alone. Color-blind readers are a meaningful fraction
 * of any SOC, and a console that encodes urgency purely in hue is unusable for them at exactly
 * the moment urgency matters.
 */
export function SeverityChip({ severity, score }: { severity: Severity; score?: number }) {
  return (
    <Chip tone={severity} title={score !== undefined ? `Score ${score}/100` : undefined}>
      {severity}
      {score !== undefined && <span className="tnum" style={{ opacity: 0.75 }}>{score}</span>}
    </Chip>
  )
}

export function StatusChip({ status }: { status: string }) {
  const tone =
    status === 'new'
      ? 'danger'
      : status === 'triage'
        ? 'warn'
        : status === 'contained' || status === 'eradicated'
          ? 'ok'
          : 'neutral'
  return <Chip tone={tone}>{status.replace(/_/g, ' ')}</Chip>
}

export function Card({
  title,
  subtitle,
  actions,
  children,
  tight,
}: {
  title?: ReactNode
  subtitle?: ReactNode
  actions?: ReactNode
  children: ReactNode
  tight?: boolean
}) {
  return (
    <section className="card">
      {(title || actions) && (
        <header className="card-head">
          <div style={{ minWidth: 0 }}>
            {title && <h2>{title}</h2>}
            {subtitle && <div className="small muted">{subtitle}</div>}
          </div>
          {actions && <div className="actions">{actions}</div>}
        </header>
      )}
      <div className={`card-body${tight ? ' tight' : ''}`}>{children}</div>
    </section>
  )
}

export function Stat({
  label,
  value,
  note,
  tone,
}: {
  label: string
  value: ReactNode
  note?: ReactNode
  tone?: 'ok' | 'warn' | 'danger'
}) {
  const color = tone ? `var(--${tone === 'danger' ? 'danger' : tone})` : undefined
  return (
    <section className="card stat">
      <div className="label">{label}</div>
      <div className="value" style={{ color }}>
        {value}
      </div>
      {note && <div className="note">{note}</div>}
    </section>
  )
}

export function Empty({
  title,
  children,
  action,
}: {
  title: string
  children?: ReactNode
  action?: ReactNode
}) {
  return (
    <div className="empty">
      <h3>{title}</h3>
      {children && <p>{children}</p>}
      {action}
    </div>
  )
}

export function Note({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warn' | 'danger' | 'ok'
  children: ReactNode
}) {
  const glyph = tone === 'danger' ? '✗' : tone === 'warn' ? '!' : tone === 'ok' ? '✓' : 'i'
  return (
    <div className={`note ${tone}`}>
      <strong aria-hidden style={{ opacity: 0.7 }}>
        {glyph}
      </strong>
      <div>{children}</div>
    </div>
  )
}

export function Bar({ pct, tone }: { pct: number; tone?: 'ok' | 'warn' | 'danger' }) {
  const clamped = Math.max(0, Math.min(100, pct))
  const color = tone ? `var(--${tone === 'danger' ? 'danger' : tone})` : 'var(--accent)'
  return (
    <div className="bar" role="meter" aria-valuenow={clamped} aria-valuemin={0} aria-valuemax={100}>
      <span style={{ width: `${clamped}%`, background: color }} />
    </div>
  )
}

export function Modal({
  title,
  children,
  onClose,
  footer,
  wide,
}: {
  title: ReactNode
  children: ReactNode
  onClose: () => void
  footer?: ReactNode
  wide?: boolean
}) {
  // Escape closes. A modal that traps an analyst mid-incident because the only way out is a
  // small × in the corner is a small cruelty that adds up.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="modal-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className={`modal${wide ? ' wide' : ''}`} role="dialog" aria-modal="true">
        <header className="modal-head">
          <h2 style={{ minWidth: 0 }}>{title}</h2>
          <div className="spacer" />
          <button className="ghost" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </header>
        <div className="modal-body">{children}</div>
        {footer && <footer className="modal-foot">{footer}</footer>}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- toasts */

type Toast = { id: number; kind: 'ok' | 'error' | 'info'; text: string }
const ToastCtx = createContext<(kind: Toast['kind'], text: string) => void>(() => {})

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const next = useRef(1)

  const push = useCallback((kind: Toast['kind'], text: string) => {
    const id = next.current++
    setToasts((t) => [...t, { id, kind, text }])
    // Errors linger: a failure an analyst blinked past is a failure they will act as though did
    // not happen.
    const ttl = kind === 'error' ? 9000 : 4000
    window.setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), ttl)
  }, [])

  return (
    <ToastCtx.Provider value={push}>
      {children}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} className={`toast ${t.kind}`} onClick={() => setToasts((x) => x.filter((y) => y.id !== t.id))}>
            {t.text}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  )
}

export function useToast() {
  return useContext(ToastCtx)
}

/* ---------------------------------------------------------------- data loading */

export interface AsyncState<T> {
  data: T | null
  error: string | null
  loading: boolean
  reload: () => void
}

/**
 * Load data, with the three states every screen actually has.
 *
 * `loading` is only true on the *first* load. A background refresh that blanks the screen every
 * few seconds makes a live console unreadable, so a reload keeps showing the previous data until
 * the new data arrives.
 */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    let canceled = false
    fn()
      .then((d) => {
        if (!canceled && alive.current) {
          setData(d)
          setError(null)
        }
      })
      .catch((e: unknown) => {
        if (canceled || !alive.current) return
        // 401 is handled globally by the API client; showing it here as well would double up.
        if (e instanceof ApiError && e.status === 401) return
        setError(e instanceof Error ? e.message : String(e))
      })
      .finally(() => {
        if (!canceled && alive.current) setLoading(false)
      })
    return () => {
      canceled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce])

  const reload = useCallback(() => setNonce((n) => n + 1), [])
  return { data, error, loading, reload }
}

/** Re-run a callback on an interval, pausing while the tab is hidden. */
export function usePoll(fn: () => void, intervalMs: number, enabled = true) {
  const saved = useRef(fn)
  useEffect(() => {
    saved.current = fn
  }, [fn])
  useEffect(() => {
    if (!enabled) return
    // Polling a hidden tab burns the analyst's battery and the server's CPU for a screen nobody
    // is looking at.
    const tick = () => {
      if (document.visibilityState === 'visible') saved.current()
    }
    const h = window.setInterval(tick, intervalMs)
    return () => window.clearInterval(h)
  }, [intervalMs, enabled])
}

/** A view over an async state that renders the right thing for each state. */
export function Async<T>({
  state,
  children,
  empty,
}: {
  state: AsyncState<T>
  children: (data: T) => ReactNode
  empty?: ReactNode
}) {
  if (state.error) {
    return (
      <Note tone="danger">
        {state.error}{' '}
        <button className="sm ghost" onClick={state.reload}>
          Retry
        </button>
      </Note>
    )
  }
  if (state.loading && state.data === null) {
    return <div className="empty muted">Loading…</div>
  }
  if (state.data === null) return <>{empty ?? null}</>
  if (Array.isArray(state.data) && state.data.length === 0 && empty) return <>{empty}</>
  return <>{children(state.data)}</>
}

/* ---------------------------------------------------------------- misc */

/** A monospace value with copy-on-click. */
export function Mono({ children, copy }: { children: string; copy?: boolean }) {
  const toast = useToast()
  if (!copy) return <span className="mono">{children}</span>
  return (
    <span
      className="mono"
      style={{ cursor: 'copy' }}
      title="Click to copy"
      onClick={() => {
        void navigator.clipboard?.writeText(children).then(
          () => toast('ok', 'Copied'),
          () => toast('error', 'Could not copy'),
        )
      }}
    >
      {children}
    </span>
  )
}

/** Sparkline over a series of values. Hand-rolled: one chart shape does not justify a library. */
export function Spark({ values, height = 28 }: { values: number[]; height?: number }) {
  const path = useMemo(() => {
    if (values.length < 2) return ''
    const max = Math.max(...values, 1)
    const w = 100
    return values
      .map((v, i) => {
        const x = (i / (values.length - 1)) * w
        const y = height - (v / max) * (height - 3) - 1.5
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`
      })
      .join(' ')
  }, [values, height])
  if (!path) return null
  return (
    <svg viewBox={`0 0 100 ${height}`} preserveAspectRatio="none" style={{ width: '100%', height }} aria-hidden>
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
    </svg>
  )
}

export function ObservableList({ observables, max = 8 }: { observables: { kind: string; value: string; role?: string }[]; max?: number }) {
  const shown = observables.slice(0, max)
  return (
    <span className="row wrap" style={{ gap: 4 }}>
      {shown.map((o, i) => (
        <Chip key={`${o.kind}:${o.value}:${i}`} tone="outline" title={`${o.kind}${o.role ? ` (${o.role})` : ''}`}>
          <span className="faint">{o.kind}</span>
          <span className="mono">{o.value}</span>
        </Chip>
      ))}
      {observables.length > max && <span className="faint small">+{observables.length - max}</span>}
    </span>
  )
}
